import { etolieMoney } from '@etolie/money';

const optionKind = (name) => /^(kolor|color|colour|couleur|farbe)$/i.test(name) ? 'color' : /^(rozmiar|size|taille|größe|grosse)$/i.test(name) ? 'size' : 'other';

// Shopify's complete option matrix, independent of the three swatches visible on a card.
export function quickAddModel(product, variantId, colorKnown = true) {
  if (product.variants.length === 1 && product.variants[0].options.length === 1 && product.variants[0].options[0] === 'Default Title') return { options: [], selected: [] };
  const options = product.options.map((option, index) => ({
    name: option.name,
    values: option.values || [...new Set(product.variants.map((variant) => variant.options[index]))],
    kind: optionKind(option.name),
  }));
  // A bag with a single size has no size decision, regardless of its translated label.
  if (/^(torebki|handbags|bags|sacs)$/i.test(product.type || '')) {
    for (const option of options) {
      if (option.kind === 'size' && option.values.length === 1) option.kind = 'other';
    }
  }
  const initial = product.variants.find((variant) => String(variant.id) === String(variantId));
  const selected = options.map((option, index) => {
    if (option.kind === 'size') return null;
    if (option.values.length === 1) return option.values[0];
    return option.kind === 'color' && colorKnown && initial ? initial.options[index] : null;
  });
  return { options, selected };
}

export function matchingVariants(product, selected, availableOnly = false) {
  return product.variants.filter((variant) => (!availableOnly || variant.available) && selected.every((value, index) => value == null || variant.options[index] === value));
}

export function selectedVariant(product, selected) {
  return selected.some((value) => value == null) ? null : matchingVariants(product, selected)[0] || null;
}

export function optionAvailable(product, selected, index, value) {
  const selection = [...selected];
  if (optionKind(product.options[index].name) === 'color') selection.fill(null);
  selection[index] = value;
  return matchingVariants(product, selection, true).length > 0;
}

export function quickAddImageUrl(src, origin) {
  if (!src) return null;
  const url = new URL(src, origin);
  url.searchParams.set('width', '200');
  return url.href;
}

if (typeof document !== 'undefined') {
  const t = (text) => window.etolieT?.(text) || text;
  const escape = (text) => String(text ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
  const root = window.Shopify?.routes?.root || '/';
  const mobile = window.matchMedia('(max-width: 749px), (max-width: 989px) and (max-height: 500px) and (hover: none) and (pointer: coarse)');
  const money = etolieMoney;
  let dialog, content, opener, context, lock, pausedVideos = [], busy = false, requestId = 0, toastTimer;

  function toast(message, showCartLink = false) {
    let node = document.getElementById('etolie-quick-add-status');
    if (!node) {
      node = document.createElement('div');
      node.id = 'etolie-quick-add-status';
      node.className = 'etolie-quick-add-toast';
      node.setAttribute('role', 'status');
    }
    const parent = opener?.closest('dialog[open]') || document.body;
    parent.append(node);
    node.textContent = message;
    if (showCartLink) {
      const link = document.createElement('a');
      link.href = root + 'cart';
      link.textContent = t('Zobacz koszyk');
      node.append(' ', link);
    }
    node.hidden = false;
    clearTimeout(toastTimer);
    if (!showCartLink) toastTimer = setTimeout(() => { node.hidden = true; }, 3200);
  }

  function close() {
    if (!dialog?.open && !lock) return;
    ++requestId;
    if (dialog?.open) dialog.close();
    if (lock) {
      Object.assign(document.body.style, lock.styles);
      window.scrollTo({ top: lock.scrollY, behavior: 'instant' });
      lock = null;
    }
    for (const video of pausedVideos) {
      if (video.isConnected && (!video.closest('dialog') || video.closest('dialog').open)) video.play().catch(() => {});
    }
    pausedVideos = [];
    if (opener?.isConnected) opener.focus({ preventScroll: true });
    context = null;
  }

  function show() {
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'etolie-quick-add';
      dialog.setAttribute('aria-labelledby', 'etolie-quick-add-title');
      dialog.innerHTML = `<button type="button" class="etolie-quick-add__close" aria-label="${escape(t('Zamknij'))}">×</button><div class="etolie-quick-add__content"></div>`;
      content = dialog.querySelector('.etolie-quick-add__content');
      dialog.querySelector('button').addEventListener('click', close);
      dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
          const rect = dialog.getBoundingClientRect();
          if (event.clientY < rect.top || event.clientX < rect.left || event.clientX > rect.right) close();
        }
        const link = event.target.closest('a');
        if (link) close();
      });
      document.body.append(dialog);
    }
    if (dialog.open) return;
    if (typeof dialog.showModal !== 'function') throw new Error('dialog-unsupported');
    dialog.showModal();
    const body = document.body;
    if (getComputedStyle(body).position !== 'fixed') {
      lock = { scrollY: window.scrollY, styles: { position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow } };
      Object.assign(body.style, { position: 'fixed', top: `-${lock.scrollY}px`, width: '100%', overflow: 'hidden' });
    }
    pausedVideos = [...document.querySelectorAll('video')].filter((video) => !video.paused);
    pausedVideos.forEach((video) => video.pause());
  }

  function currentUrl(trigger) {
    const card = trigger.closest('.etolie-card, product-card, .resource-card');
    return new URL(card?.querySelector('[data-etolie-card-link]')?.href || trigger.dataset.etolieQuickAdd || trigger.href, location.origin);
  }

  async function open(trigger) {
    if (busy) return;
    opener = trigger;
    opener.closest('etolie-listing-stream')?.rememberScroll?.();
    const url = currentUrl(trigger);
    // 12.10.2026 (audyt SEO): karta w kolorze, który otwiera adres główny, linkuje go bez ?variant=.
    // Wariant koloru karty podaje data-etolie-variant; wewnętrzny adres okna dostaje go z kolor=1
    // jak dotąd, więc kolor, rozmiary i linki „Opis i wymiary” / „Tabela rozmiarów” są bez zmian.
    const cardVariant = trigger.closest('.etolie-card')?.dataset.etolieVariant;
    if (cardVariant && !url.searchParams.has('variant')) {
      url.searchParams.set('variant', cardVariant);
      url.searchParams.set('kolor', '1');
    }
    const handle = url.pathname.match(/\/products\/([^/]+)/)?.[1];
    if (!handle || url.origin !== location.origin) { location.assign(url.href); return; }
    const id = ++requestId;
    try {
      show();
    } catch (_error) {
      close();
      location.assign(url.href);
      return;
    }
    content.innerHTML = `<h2 id="etolie-quick-add-title" class="etolie-quick-add__loading" role="status">${escape(t('Wczytuję produkt…'))}</h2>`;
    dialog.setAttribute('aria-busy', 'true');
    try {
      // The shared swatch dictionary uses original Polish names, while labels stay localized.
      const sourceRoot = /^\/(en|fr)-/.test(root) ? root.replace(/^\/(en|fr)-/, '/pl-') : '/';
      const sourceRequest = document.documentElement.lang.split('-')[0] !== 'pl'
        ? fetch(`${sourceRoot}products/${handle}.js`).then((response) => response.ok ? response.json() : null).catch(() => null)
        : Promise.resolve(null);
      const response = await fetch(`${root}products/${handle}.js`);
      if (!response.ok) throw new Error('product');
      const product = await response.json();
      const sourceProduct = await sourceRequest;
      if (id !== requestId || !dialog.open) return;
      if (!product.variants?.length || product.variants.length >= 250 || product.requires_selling_plan) throw new Error('full-product-required');
      const model = quickAddModel({ ...product, type: sourceProduct?.type || product.type }, url.searchParams.get('variant'), trigger.dataset.quickColorKnown !== 'false');
      context = { product, sourceProduct, ...model, url, blockedVariant: null, sizeGuideColors: [] };
      render();
      if (model.options.some((option) => option.kind === 'size')) loadSizeGuide(context, handle);
      // Only a product with no size option and one exact available combination can skip selection.
      const variant = selectedVariant(product, model.selected);
      if (!model.options.some((option) => option.kind === 'size') && variant?.available) await add();
    } catch (_error) {
      if (id !== requestId || !dialog.open) return;
      content.innerHTML = `<h2 id="etolie-quick-add-title">${escape(t('Wybierz produkt'))}</h2><p>${escape(t('Nie udało się wczytać produktu.'))}</p><a class="etolie-quick-add__submit" href="${escape(url.href)}">${escape(t('Zobacz produkt'))}</a>`;
    } finally {
      dialog.removeAttribute('aria-busy');
    }
  }

  async function loadSizeGuide(openedContext, handle) {
    try {
      const response = await fetch(`${root}products/${handle}?section_id=etolie-quick-add-guide`);
      if (!response.ok) return;
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = JSON.parse(document.querySelector('[data-etolie-quick-add-guide]')?.textContent || 'null');
      if (context !== openedContext || !dialog.open || !Array.isArray(data?.colors)) return;
      openedContext.sizeGuideColors = data.colors;
      update();
    } catch (_error) {
      // Keep the existing product-details link when dimensions cannot be verified.
    }
  }

  function productUrl(variant) {
    const url = new URL(context.url);
    url.hash = '';
    if (variant) url.searchParams.set('variant', variant.id);
    return url;
  }

  function render() {
    if (!context || !dialog.open) return;
    const { product, options, selected } = context;
    content.innerHTML = `<div class="etolie-quick-add__summary"><img class="etolie-quick-add__image" alt="${escape(product.title)}" width="76" height="94"><div><h2 id="etolie-quick-add-title">${escape(product.title)}</h2><p class="etolie-quick-add__price"></p></div></div><div class="etolie-quick-add__options"></div><p class="etolie-quick-add__notice" role="status"></p><button type="button" class="etolie-quick-add__submit"></button><a class="etolie-quick-add__details">${escape(t('Opis i wymiary produktu'))}</a>`;
    const group = content.querySelector('.etolie-quick-add__options');
    options.forEach((option, index) => {
      if (option.kind === 'other' && option.values.length === 1) return;
      const fieldset = document.createElement('fieldset');
      fieldset.className = `etolie-quick-add__option etolie-quick-add__option--${option.kind}`;
      const label = option.kind === 'color' ? t('Kolor') : option.kind === 'size' ? t('Rozmiar') : option.name;
      fieldset.innerHTML = `<legend>${escape(label)}<span data-selected-label></span></legend><div class="etolie-quick-add__choices"></div>`;
      if (option.kind === 'size') {
        const guide = document.createElement('a');
        guide.className = 'etolie-quick-add__guide';
        guide.hidden = true;
        guide.textContent = t('Tabela rozmiarów');
        fieldset.querySelector('legend').append(guide);
      }
      option.values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'etolie-quick-add__choice';
        button.dataset.optionIndex = index;
        button.dataset.optionValue = value;
        button.textContent = value;
        if (option.kind === 'color') {
          const variant = product.variants.find((item) => item.options[index] === value);
          const sourceVariant = context.sourceProduct?.variants.find((item) => String(item.id) === String(variant?.id));
          const swatch = window.etolieResolveSwatch?.(variant?.id, sourceVariant?.options[index] || value);
          if (swatch) {
            button.classList.add('etolie-quick-add__choice--swatch');
            button.textContent = '';
            const chip = document.createElement('span');
            chip.style.background = swatch.background;
            if (swatch.background.includes('radial-gradient')) chip.style.backgroundSize = '6px 6px';
            button.append(chip);
          }
        }
        button.addEventListener('click', () => {
          if (busy) return;
          selected[index] = value;
          context.blockedVariant = null;
          update();
        });
        fieldset.querySelector('.etolie-quick-add__choices').append(button);
      });
      group.append(fieldset);
    });
    content.querySelector('.etolie-quick-add__submit').addEventListener('click', add);
    update();
  }

  function update(message = '') {
    if (!context || !dialog.open) return;
    const { product, options, selected } = context;
    const variant = selectedVariant(product, selected);
    const matches = matchingVariants(product, selected);
    const colorIndex = options.findIndex((option) => option.kind === 'color');
    const colorVariants = product.variants.filter((item) => colorIndex < 0 || selected[colorIndex] === item.options[colorIndex]);
    const representative = variant || matches[0] || colorVariants[0];
    const exactImage = representative?.featured_image?.src;
    const image = content.querySelector('img');
    const src = exactImage || (colorIndex < 0 || options[colorIndex].values.length === 1 ? product.featured_image : null);
    const imageSrc = quickAddImageUrl(src, location.origin);
    image.hidden = !imageSrc;
    if (imageSrc && image.getAttribute('src') !== imageSrc) image.src = imageSrc;
    const priced = variant ? [variant] : matches.length ? matches : colorVariants.length ? colorVariants : product.variants;
    const prices = priced.map((item) => item.price);
    content.querySelector('.etolie-quick-add__price').textContent = `${Math.min(...prices) !== Math.max(...prices) ? t('od') + ' ' : ''}${money(Math.min(...prices))}`;
    content.querySelectorAll('[data-option-index]').forEach((button) => {
      const index = Number(button.dataset.optionIndex);
      const value = button.dataset.optionValue;
      const available = optionAvailable(product, selected, index, value);
      button.setAttribute('aria-pressed', String(selected[index] === value));
      button.setAttribute('aria-label', `${value}${available ? '' : ', ' + t('Niedostępny')}`);
      button.classList.toggle('is-unavailable', !available);
      button.disabled = busy || !available;
      const label = button.closest('fieldset').querySelector('[data-selected-label]');
      label.textContent = selected[index] && options[index].kind !== 'size' ? `: ${selected[index]}` : '';
    });
    const button = content.querySelector('.etolie-quick-add__submit');
    const missingSize = options.some((option, index) => option.kind === 'size' && selected[index] == null);
    const missingColor = options.some((option, index) => option.kind === 'color' && selected[index] == null);
    const unavailable = !variant?.available && !selected.some((value) => value == null);
    button.disabled = busy || !variant?.available || context.blockedVariant === variant?.id;
    button.textContent = busy ? t('Dodaję…') : missingColor ? t('Wybierz kolor') : missingSize ? t('Wybierz rozmiar') : unavailable ? t('Niedostępny') : !variant ? t('Wybierz opcje') : context.blockedVariant === variant.id ? t('Nie ma więcej sztuk') : t('Dodaj do koszyka');
    content.querySelector('.etolie-quick-add__notice').textContent = message || (unavailable ? t('Wybrany wariant jest niedostępny. Wybierz inną opcję.') : '');
    const url = productUrl(representative);
    content.querySelector('.etolie-quick-add__details').href = url.href;
    const guide = content.querySelector('.etolie-quick-add__guide');
    if (guide) {
      const guideColor = colorIndex < 0 ? '' : selected[colorIndex];
      guide.hidden = !context.sizeGuideColors.includes(guideColor);
      if (guide.hidden) guide.removeAttribute('href');
      else { url.hash = 'etolie-size-details'; guide.href = url.href; }
    }
  }

  async function readCart() {
    const response = await fetch(`${root}cart.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error('cart-read');
    return response.json();
  }

  function notifyCart(cart, variantId, sections) {
    document.dispatchEvent(new CustomEvent('cart:update', { detail: {
      resource: cart, sourceId: 'etolie-mobile-quick-add',
      data: { source: 'etolie-mobile-quick-add', itemCount: cart.item_count, variantId, sections, suppressCartDrawer: true },
    } }));
  }

  function uncertain(message) {
    if (!dialog?.open) { toast(message, true); return; }
    content.innerHTML = `<h2 id="etolie-quick-add-title">${escape(t('Koszyk'))}</h2><p role="status">${escape(message)}</p><a class="etolie-quick-add__submit" href="${escape(root + 'cart')}">${escape(t('Zobacz koszyk'))}</a>`;
    context = null;
  }

  async function add() {
    if (busy || !context) return;
    const variant = selectedVariant(context.product, context.selected);
    if (!variant?.available || context.blockedVariant === variant.id) return;
    busy = true;
    const addingContext = context;
    update();
    let before, posted = false, sent = false, rejected = false, message = '';
    try {
      // This baseline also lets a lost POST response be reconciled without adding twice.
      before = await readCart();
      const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId))].slice(0, 5);
      sent = true;
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], sections: sectionIds.join(','), sections_url: location.pathname }),
      });
      if (!response.ok) {
        if (response.status === 422) {
          rejected = true;
          addingContext.blockedVariant = variant.id;
          message = t('Nie ma więcej sztuk');
          const cart = await readCart();
          notifyCart(cart, variant.id);
          return;
        }
        throw new Error('cart-add');
      }
      posted = true;
      const added = await response.json();
      const cart = await readCart();
      notifyCart(cart, variant.id, added.sections);
      if (context === addingContext) close();
      toast(t('Dodano do koszyka'));
    } catch (_error) {
      if (rejected) {
        message = t('Nie ma więcej sztuk');
      } else if (posted) {
        uncertain(t('Produkt dodano. Otwórz koszyk, aby zobaczyć aktualną zawartość.'));
      } else if (sent) {
        try {
          const cart = await readCart();
          const quantity = (resource) => resource.items.filter((item) => String(item.variant_id) === String(variant.id)).reduce((sum, item) => sum + item.quantity, 0);
          notifyCart(cart, variant.id);
          if (quantity(cart) > quantity(before)) {
            if (context === addingContext) close();
            toast(t('Dodano do koszyka'));
          } else {
            uncertain(t('Nie udało się potwierdzić dodania. Sprawdź koszyk przed ponowną próbą.'));
          }
        } catch (_readError) {
          uncertain(t('Nie udało się potwierdzić dodania. Sprawdź koszyk przed ponowną próbą.'));
        }
      } else {
        message = t('Nie udało się połączyć. Spróbuj ponownie.');
      }
    } finally {
      busy = false;
      if (context === addingContext) update(message);
    }
  }

  document.addEventListener('click', (event) => {
    if (!mobile.matches || !(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-etolie-quick-add]');
    if (!trigger) return;
    if (typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (trigger.closest('[data-etolie-card-gallery-media]')?.__swiped || busy) return;
    open(trigger);
  }, true);
  mobile.addEventListener('change', () => { if (!mobile.matches) close(); });
  window.addEventListener('pagehide', close);
}
